import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import * as Clipboard from "expo-clipboard";
import { useFocusEffect } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";

import { PermissionsPicker } from "../../../components/admin/PermissionsPicker";
import type { AppRole } from "../../../lib/auth";
import {
  approveEmployee,
  forceSignOutUser,
  hasAdminPermission,
  listAdminUsers,
  listUserSessions,
  lockUser,
  rejectEmployee,
  resetUserMfa,
  setUserPermissions,
  unlockUser,
  type AdminTeamUserRow,
  type SessionRow,
} from "../../../lib/admin";
import { defaultPermissionsForRole, isSuperAdminRole } from "../../../lib/adminPermissions";
import { buildInviteUrl, createInvite } from "../../../lib/invites";
import { useAuth } from "../../../lib/authContext";
import {
  AdminButton,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminPageHeader,
} from "../../../components/admin/ui";

const THEME = PANE_THEMES.Admin;

const BLACK = THEME.pageBg;
const FOREST_MID = THEME.surfaceRaisedBg;
const FOREST = THEME.sidebarBg;
const AMBER = THEME.accent;
const SAGE = COLORS.SAGE.hex;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const RED = THEME.destructive;
const BORDER = "rgba(255,255,255,0.07)";

const TZ = "America/New_York";

const INVITE_ROLES: AppRole[] = ["admin_licensing", "admin_rm", "super_admin"];

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: TZ });
}

function fmtFullTs(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: TZ })} · ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: TZ })}`;
}

function makeInitials(u: AdminTeamUserRow): string {
  const src = (u.full_name ?? u.email ?? "?").trim();
  if (!src) return "?";
  const parts = src.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export default function AdminTeamScreen() {
  const { refetchProfile } = useAuth();
  const [canManage, setCanManage] = useState<boolean | null>(null);
  const [pending, setPending] = useState<AdminTeamUserRow[]>([]);
  const [active, setActive] = useState<AdminTeamUserRow[]>([]);
  const [rejected, setRejected] = useState<AdminTeamUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<AppRole>("admin_licensing");
  const [invitePerms, setInvitePerms] = useState<string[]>([]);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  const [inviteExpires, setInviteExpires] = useState("");

  const [permUser, setPermUser] = useState<AdminTeamUserRow | null>(null);
  const [permEdit, setPermEdit] = useState(false);
  const [permDraft, setPermDraft] = useState<string[]>([]);
  const [permSaving, setPermSaving] = useState(false);

  const [sessionUser, setSessionUser] = useState<AdminTeamUserRow | null>(null);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [sessionLoading, setSessionLoading] = useState(false);

  const [lockUserRow, setLockUserRow] = useState<AdminTeamUserRow | null>(null);
  const [lockReason, setLockReason] = useState("");

  // Per-row armed state for two-click destructive actions.
  const [armedAction, setArmedAction] = useState<{ rowId: string; action: string } | null>(null);
  const armedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasLoadedRef = useRef(false);

  function armOrFire(rowId: string, action: string, fire: () => void) {
    const cur = armedAction;
    if (cur && cur.rowId === rowId && cur.action === action) {
      if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
      setArmedAction(null);
      fire();
    } else {
      setArmedAction({ rowId, action });
      if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
      armedTimerRef.current = setTimeout(() => setArmedAction(null), 4000);
    }
  }

  const isArmed = useCallback(
    (rowId: string, action: string) =>
      Boolean(armedAction && armedAction.rowId === rowId && armedAction.action === action),
    [armedAction]
  );

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      const [allowed, pend, act, rej] = await Promise.all([
        hasAdminPermission("manage_team"),
        listAdminUsers({ status: "pending" }),
        listAdminUsers({ status: "active" }),
        listAdminUsers({ status: "rejected" }),
      ]);
      setCanManage(allowed);
      setPending(pend);
      setActive(act);
      setRejected(rej);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load team");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  useEffect(
    () => () => {
      if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
    },
    []
  );

  function openInvite() {
    setInviteOpen(true);
    setInviteEmail("");
    setInviteRole("admin_licensing");
    setInvitePerms(defaultPermissionsForRole("admin_licensing"));
    setInviteUrl("");
    setInviteExpires("");
    setErr(null);
  }

  function onInviteRolePress(r: AppRole) {
    setInviteRole(r);
    setInvitePerms(defaultPermissionsForRole(r));
  }

  async function submitInvite() {
    if (!inviteEmail.trim()) {
      setErr("Email is required.");
      return;
    }
    setInviteBusy(true);
    setErr(null);
    try {
      const perms = isSuperAdminRole(inviteRole) ? defaultPermissionsForRole("super_admin") : invitePerms;
      const row = await createInvite({
        kind: "employee",
        email: inviteEmail.trim(),
        role: inviteRole,
        permissions: perms,
      });
      setInviteUrl(buildInviteUrl(row.token));
      setInviteExpires(fmtFullTs(row.expires_at));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Invite failed");
    } finally {
      setInviteBusy(false);
    }
  }

  function openPermissions(u: AdminTeamUserRow, edit: boolean) {
    setPermUser(u);
    setPermEdit(edit);
    setPermDraft([...(u.admin_permissions ?? [])]);
  }

  async function savePermissions() {
    if (!permUser) return;
    setPermSaving(true);
    setErr(null);
    try {
      await setUserPermissions(permUser.id, permDraft);
      setPermUser(null);
      await load();
      await refetchProfile();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setPermSaving(false);
    }
  }

  async function openSessions(u: AdminTeamUserRow) {
    setSessionUser(u);
    setSessionLoading(true);
    setSessions([]);
    try {
      setSessions(await listUserSessions(u.id));
    } finally {
      setSessionLoading(false);
    }
  }

  async function refreshSessions() {
    if (!sessionUser) return;
    setSessionLoading(true);
    try {
      setSessions(await listUserSessions(sessionUser.id));
    } finally {
      setSessionLoading(false);
    }
  }

  async function forceAllSessions() {
    if (!sessionUser) return;
    setErr(null);
    try {
      await forceSignOutUser(sessionUser.id);
      await refreshSessions();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Force sign out failed");
    }
  }

  if (canManage === false) {
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center", backgroundColor: BLACK, padding: 32 }}>
        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: WS, textAlign: "center" }}>
          Team management requires the manage_team permission.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: BLACK }} contentContainerStyle={{ paddingBottom: 40 }}>
      <View style={{ padding: 24, maxWidth: 1080 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Platform · Internal team"
          title="Internal team"
          subtitle="Internal Coravu admin roles, access, and invites."
          actions={<AdminButton theme={THEME} label="+ Invite team member" onPress={openInvite} />}
        />

        {err ? (
          <View style={{ marginBottom: 14 }}>
            <AdminError theme={THEME} message={err} />
          </View>
        ) : null}

        {loading || canManage === null ? (
          <AdminLoading theme={THEME} />
        ) : (
          <>
            <SectionLabel>Pending approval</SectionLabel>
            {pending.length === 0 ? (
              <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
                No pending team members.
              </Text>
            ) : (
              <View style={{ marginTop: 8, gap: 8 }}>
                {pending.map((u) => (
                  <PendingTeamRow
                    key={u.id}
                    u={u}
                    onApprove={() =>
                      armOrFire(u.id, "approve", async () => {
                        try {
                          await approveEmployee(u.id);
                          await load();
                        } catch (e) {
                          setErr(e instanceof Error ? e.message : "Approve failed");
                        }
                      })
                    }
                    onReject={() =>
                      armOrFire(u.id, "reject", async () => {
                        try {
                          await rejectEmployee(u.id);
                          await load();
                        } catch (e) {
                          setErr(e instanceof Error ? e.message : "Reject failed");
                        }
                      })
                    }
                    onViewPerms={() => openPermissions(u, false)}
                    isArmedApprove={isArmed(u.id, "approve")}
                    isArmedReject={isArmed(u.id, "reject")}
                  />
                ))}
              </View>
            )}

            <SectionLabel style={{ marginTop: 26 }}>Active team</SectionLabel>
            <View style={{ marginTop: 8, gap: 8 }}>
              {active.length === 0 ? (
                <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: WM }}>
                  No active team members yet.
                </Text>
              ) : (
                active.map((u) => (
                  <ActiveTeamRow
                    key={u.id}
                    u={u}
                    onEditPerms={() => openPermissions(u, true)}
                    onSessions={() => void openSessions(u)}
                    onForceSignOut={() =>
                      armOrFire(u.id, "force_signout", async () => {
                        try {
                          await forceSignOutUser(u.id);
                          await load();
                        } catch (e) {
                          setErr(e instanceof Error ? e.message : "Failed");
                        }
                      })
                    }
                    onLock={() => {
                      setLockUserRow(u);
                      setLockReason("");
                    }}
                    onUnlock={() =>
                      void (async () => {
                        try {
                          await unlockUser(u.id);
                          await load();
                        } catch (e) {
                          setErr(e instanceof Error ? e.message : "Unlock failed");
                        }
                      })()
                    }
                    onResetMfa={() =>
                      armOrFire(u.id, "reset_mfa", async () => {
                        try {
                          await resetUserMfa(u.id);
                          await load();
                        } catch (e) {
                          setErr(e instanceof Error ? e.message : "Reset failed");
                        }
                      })
                    }
                    isArmedSignOut={isArmed(u.id, "force_signout")}
                    isArmedResetMfa={isArmed(u.id, "reset_mfa")}
                  />
                ))
              )}
            </View>

            <SectionLabel style={{ marginTop: 26 }}>Rejected</SectionLabel>
            <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 11.5, color: WM }}>
              Applications you previously rejected. Reinstate moves them straight to Active.
            </Text>
            {rejected.length === 0 ? (
              <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
                No rejected applications.
              </Text>
            ) : (
              <View style={{ marginTop: 8, gap: 8 }}>
                {rejected.map((u) => (
                  <RejectedTeamRow
                    key={u.id}
                    u={u}
                    onReinstate={() =>
                      armOrFire(u.id, "reinstate", async () => {
                        try {
                          await approveEmployee(u.id);
                          await load();
                        } catch (e) {
                          setErr(e instanceof Error ? e.message : "Reinstate failed");
                        }
                      })
                    }
                    isArmed={isArmed(u.id, "reinstate")}
                  />
                ))}
              </View>
            )}
          </>
        )}
      </View>

      {/* Invite modal */}
      <Modal visible={inviteOpen} transparent animationType="fade" onRequestClose={() => setInviteOpen(false)}>
        <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
          <ScrollView contentContainerStyle={{ paddingVertical: 24 }} keyboardShouldPersistTaps="handled">
            <View
              style={{
                maxWidth: 560,
                width: "100%",
                alignSelf: "center",
                backgroundColor: FOREST_MID,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: BORDER,
                padding: 22,
              }}
            >
              <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
                Invite team member
              </Text>
              {!inviteUrl ? (
                <>
                  <View style={{ marginTop: 12, flexDirection: "row", alignItems: "center", gap: 4 }}>
                    <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: WM }}>
                      Email
                    </Text>
                    <Text style={{ color: RED, fontSize: 10 }}>*</Text>
                  </View>
                  <DarkInput
                    value={inviteEmail}
                    onChange={setInviteEmail}
                    placeholder="colleague@coravu.com"
                    autoCapitalize="none"
                    keyboardType="email-address"
                  />
                  <Text style={{ marginTop: 14, fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: WM }}>
                    Role
                  </Text>
                  <View style={{ marginTop: 8, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                    {INVITE_ROLES.map((r) => {
                      const active = inviteRole === r;
                      return (
                        <Pressable
                          key={r}
                          onPress={() => onInviteRolePress(r)}
                          style={[
                            {
                              paddingHorizontal: 12,
                              paddingVertical: 6,
                              borderRadius: 999,
                              borderWidth: 1,
                              borderColor: active ? AMBER : BORDER,
                              backgroundColor: active ? "rgba(181,135,58,0.12)" : "transparent",
                            } as ViewStyle,
                            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                          ]}
                        >
                          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: active ? AMBER : WS }}>
                            {r}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                  <Text style={{ marginTop: 14, fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: WM }}>
                    Permissions
                  </Text>
                  <ScrollView style={{ marginTop: 8, maxHeight: 240 }}>
                    <PermissionsPicker role={inviteRole} permissions={invitePerms} onChange={setInvitePerms} />
                  </ScrollView>
                  <Pressable
                    onPress={() => void submitInvite()}
                    disabled={inviteBusy}
                    style={[
                      {
                        marginTop: 16,
                        paddingVertical: 11,
                        borderRadius: 3,
                        backgroundColor: AMBER,
                        alignItems: "center",
                        opacity: inviteBusy ? 0.5 : 1,
                      } as ViewStyle,
                      Platform.OS === "web" && !inviteBusy ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    {inviteBusy ? (
                      <ActivityIndicator size="small" color={FOREST} />
                    ) : (
                      <Text
                        style={{
                          fontFamily: FONTS.bodyBold,
                          fontSize: 11.5,
                          letterSpacing: 1.4,
                          textTransform: "uppercase",
                          color: FOREST,
                        }}
                      >
                        Create invite
                      </Text>
                    )}
                  </Pressable>
                </>
              ) : (
                <>
                  <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 12, color: WS }}>
                    Share this link (expires {inviteExpires})
                  </Text>
                  <Text
                    selectable
                    style={{
                      marginTop: 8,
                      paddingHorizontal: 12,
                      paddingVertical: 10,
                      backgroundColor: BLACK,
                      borderWidth: 1,
                      borderColor: BORDER,
                      borderRadius: 4,
                      fontFamily: Platform.OS === "web" ? ("'JetBrains Mono', Menlo, monospace" as string) : "Menlo",
                      fontSize: 11.5,
                      color: LINEN,
                      lineHeight: 17,
                    }}
                  >
                    {inviteUrl}
                  </Text>
                  <Pressable
                    onPress={() => void Clipboard.setStringAsync(inviteUrl)}
                    style={[
                      {
                        marginTop: 12,
                        paddingVertical: 10,
                        borderRadius: 3,
                        backgroundColor: AMBER,
                        alignItems: "center",
                      } as ViewStyle,
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <Text
                      style={{
                        fontFamily: FONTS.bodyBold,
                        fontSize: 11.5,
                        letterSpacing: 1.4,
                        textTransform: "uppercase",
                        color: FOREST,
                      }}
                    >
                      Copy link
                    </Text>
                  </Pressable>
                </>
              )}
              <Pressable
                onPress={() => setInviteOpen(false)}
                style={[
                  { marginTop: 12, alignItems: "center", paddingVertical: 6 } as ViewStyle,
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WS }}>Close</Text>
              </Pressable>
            </View>
          </ScrollView>
        </View>
      </Modal>

      {/* Permissions modal */}
      <Modal visible={!!permUser} transparent animationType="fade" onRequestClose={() => setPermUser(null)}>
        <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
          <View
            style={{
              maxWidth: 560,
              width: "100%",
              alignSelf: "center",
              backgroundColor: FOREST_MID,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: BORDER,
              padding: 22,
            }}
          >
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
              {permEdit ? "Edit permissions" : "Permissions"}
            </Text>
            <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 11.5, color: WM }}>
              {permUser?.email}
            </Text>
            <ScrollView style={{ marginTop: 12, maxHeight: 280 }}>
              {permEdit ? (
                <PermissionsPicker role={permUser?.role ?? null} permissions={permDraft} onChange={setPermDraft} />
              ) : (
                <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, color: WS }}>
                  {(permUser?.admin_permissions ?? []).length ? permUser!.admin_permissions!.join(", ") : "—"}
                </Text>
              )}
            </ScrollView>
            {permEdit ? (
              <Pressable
                onPress={() => void savePermissions()}
                disabled={permSaving}
                style={[
                  {
                    marginTop: 16,
                    paddingVertical: 10,
                    borderRadius: 3,
                    backgroundColor: AMBER,
                    alignItems: "center",
                    opacity: permSaving ? 0.5 : 1,
                  } as ViewStyle,
                  Platform.OS === "web" && !permSaving ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                {permSaving ? (
                  <ActivityIndicator size="small" color={FOREST} />
                ) : (
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 11.5,
                      letterSpacing: 1.4,
                      textTransform: "uppercase",
                      color: FOREST,
                    }}
                  >
                    Save
                  </Text>
                )}
              </Pressable>
            ) : null}
            <Pressable
              onPress={() => setPermUser(null)}
              style={[
                { marginTop: 10, alignItems: "center", paddingVertical: 6 } as ViewStyle,
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WS }}>Close</Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      {/* Sessions modal */}
      <Modal visible={!!sessionUser} transparent animationType="fade" onRequestClose={() => setSessionUser(null)}>
        <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
          <View
            style={{
              maxWidth: 640,
              width: "100%",
              alignSelf: "center",
              backgroundColor: FOREST_MID,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: BORDER,
              padding: 22,
              maxHeight: Platform.OS === "web" ? ("82vh" as unknown as number) : 720,
            }}
          >
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
              Sessions
            </Text>
            <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 11.5, color: WM }}>
              {sessionUser?.email}
            </Text>
            <Pressable
              onPress={() => void forceAllSessions()}
              style={[
                {
                  marginTop: 14,
                  alignSelf: "flex-start",
                  paddingHorizontal: 12,
                  paddingVertical: 7,
                  borderRadius: 3,
                  borderWidth: 1,
                  borderColor: RED,
                  backgroundColor: "rgba(220,80,80,0.10)",
                } as ViewStyle,
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 10.5,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: RED,
                }}
              >
                Force sign out all
              </Text>
            </Pressable>
            {sessionLoading ? (
              <ActivityIndicator style={{ marginTop: 12 }} size="small" color={AMBER} />
            ) : (
              <ScrollView style={{ marginTop: 12, maxHeight: 380 }}>
                {sessions.map((s) => (
                  <View
                    key={s.session_id}
                    style={{
                      paddingVertical: 8,
                      borderBottomWidth: 1,
                      borderBottomColor: BORDER,
                    }}
                  >
                    <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: WM }}>
                      Started {fmtFullTs(s.created_at)}
                    </Text>
                    <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: WM }}>
                      Last active {fmtFullTs(s.updated_at)}
                    </Text>
                    <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: WS }} numberOfLines={2}>
                      {(s.user_agent ?? "—").slice(0, 140)}
                    </Text>
                    <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: WM }}>
                      {s.ip ?? "—"}
                    </Text>
                  </View>
                ))}
              </ScrollView>
            )}
            <Pressable
              onPress={() => setSessionUser(null)}
              style={[
                { marginTop: 12, alignItems: "center", paddingVertical: 6 } as ViewStyle,
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WS }}>Close</Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      {/* Lock modal */}
      <Modal visible={!!lockUserRow} transparent animationType="fade" onRequestClose={() => setLockUserRow(null)}>
        <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
          <View
            style={{
              maxWidth: 480,
              width: "100%",
              alignSelf: "center",
              backgroundColor: FOREST_MID,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: BORDER,
              padding: 22,
            }}
          >
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
              Lock account
            </Text>
            <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 11.5, color: WM }}>
              {lockUserRow?.email}
            </Text>
            <DarkInput
              value={lockReason}
              onChange={setLockReason}
              placeholder="Reason (optional)"
            />
            <Pressable
              onPress={() =>
                void (async () => {
                  if (!lockUserRow) return;
                  try {
                    await lockUser(lockUserRow.id, lockReason.trim() || undefined);
                    setLockUserRow(null);
                    await load();
                  } catch (e) {
                    setErr(e instanceof Error ? e.message : "Lock failed");
                  }
                })()
              }
              style={[
                {
                  marginTop: 14,
                  paddingVertical: 10,
                  borderRadius: 3,
                  backgroundColor: RED,
                  alignItems: "center",
                } as ViewStyle,
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 11.5,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: FOREST,
                }}
              >
                Lock account
              </Text>
            </Pressable>
            <Pressable
              onPress={() => setLockUserRow(null)}
              style={[
                { marginTop: 10, alignItems: "center", paddingVertical: 6 } as ViewStyle,
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WS }}>Cancel</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
}

function SectionLabel({ children, style }: { children: string; style?: ViewStyle }) {
  return (
    <Text
      style={[
        {
          marginTop: 26,
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: AMBER,
        },
        style as object,
      ]}
    >
      {children}
    </Text>
  );
}

function Avatar({ u }: { u: AdminTeamUserRow }) {
  if (u.avatar_url) {
    return (
      <Image
        source={{ uri: u.avatar_url }}
        style={{ width: 40, height: 40, borderRadius: 20 }}
      />
    );
  }
  return (
    <View
      style={{
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: "rgba(181,135,58,0.16)",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Text style={{ fontFamily: FONTS.displaySemiBold, fontSize: 14, color: AMBER }}>
        {makeInitials(u)}
      </Text>
    </View>
  );
}

function PendingTeamRow({
  u,
  onApprove,
  onReject,
  onViewPerms,
  isArmedApprove,
  isArmedReject,
}: {
  u: AdminTeamUserRow;
  onApprove: () => void;
  onReject: () => void;
  onViewPerms: () => void;
  isArmedApprove: boolean;
  isArmedReject: boolean;
}) {
  const [appOpen, setAppOpen] = useState(false);
  return (
    <View
      style={{
        padding: 14,
        borderRadius: 6,
        backgroundColor: FOREST_MID,
        borderWidth: 1,
        borderColor: BORDER,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
        <Avatar u={u} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: LINEN }} numberOfLines={1}>
            {u.full_name ?? "—"}
          </Text>
          <Text style={{ marginTop: 1, fontFamily: FONTS.body, fontSize: 11.5, color: WS }} numberOfLines={1}>
            {u.email}
          </Text>
          <Text style={{ marginTop: 1, fontFamily: FONTS.body, fontSize: 11, color: WM }} numberOfLines={1}>
            {u.role} · invited {fmtDate(u.role_assigned_at)}
          </Text>
        </View>
      </View>
      <View style={{ marginTop: 10, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
        <RowAction
          label={isArmedApprove ? "Click again to approve" : "Approve"}
          tint={isArmedApprove ? SAGE : AMBER}
          onPress={onApprove}
        />
        <RowAction
          label={isArmedReject ? "Click again to reject" : "Reject"}
          tint={RED}
          onPress={onReject}
        />
        <RowAction
          label={appOpen ? "Hide application" : "View application"}
          onPress={() => setAppOpen((s) => !s)}
        />
        <RowAction label="View permissions" onPress={onViewPerms} />
      </View>
      {appOpen ? (
        <View
          style={{
            marginTop: 10,
            paddingHorizontal: 12,
            paddingVertical: 10,
            borderRadius: 4,
            backgroundColor: BLACK,
            borderWidth: 1,
            borderColor: BORDER,
          }}
        >
          <ApplicationField label="Role applied for">{u.role}</ApplicationField>
          <ApplicationField label="Email">{u.email ?? "—"}</ApplicationField>
          <ApplicationField label="Invited">{fmtFullTs(u.role_assigned_at)}</ApplicationField>
          <ApplicationField label="Profile created">{fmtFullTs(u.profile_created_at)}</ApplicationField>
          <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 10.5, color: WL, fontStyle: "italic" }}>
            We don&apos;t capture a free-form motivation field on the application yet — when that ships it&apos;ll appear here.
          </Text>
        </View>
      ) : null}
    </View>
  );
}

function ApplicationField({ label, children }: { label: string; children: string }) {
  return (
    <View style={{ flexDirection: "row", marginTop: 4, gap: 8 }}>
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WM, minWidth: 110 }}>{label}</Text>
      <Text style={{ flex: 1, fontFamily: FONTS.body, fontSize: 11.5, color: WS }}>{children}</Text>
    </View>
  );
}

function ActiveTeamRow({
  u,
  onEditPerms,
  onSessions,
  onForceSignOut,
  onLock,
  onUnlock,
  onResetMfa,
  isArmedSignOut,
  isArmedResetMfa,
}: {
  u: AdminTeamUserRow;
  onEditPerms: () => void;
  onSessions: () => void;
  onForceSignOut: () => void;
  onLock: () => void;
  onUnlock: () => void;
  onResetMfa: () => void;
  isArmedSignOut: boolean;
  isArmedResetMfa: boolean;
}) {
  const permCount = u.admin_permissions?.length ?? 0;
  return (
    <View
      style={{
        padding: 14,
        borderRadius: 6,
        backgroundColor: FOREST_MID,
        borderWidth: 1,
        borderColor: BORDER,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
        <Avatar u={u} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: LINEN }} numberOfLines={1}>
            {u.full_name ?? "—"}
          </Text>
          <Text style={{ marginTop: 1, fontFamily: FONTS.body, fontSize: 11.5, color: WS }} numberOfLines={1}>
            {u.email}
          </Text>
          <Text style={{ marginTop: 1, fontFamily: FONTS.body, fontSize: 11, color: WM }} numberOfLines={1}>
            {u.role} · {permCount} permission{permCount === 1 ? "" : "s"}
          </Text>
        </View>
      </View>
      <View style={{ marginTop: 10, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
        <RowAction label="Edit permissions" onPress={onEditPerms} />
        <RowAction
          label={isArmedSignOut ? "Click again" : "Force sign out"}
          tint={isArmedSignOut ? RED : undefined}
          onPress={onForceSignOut}
        />
        <RowAction label="Lock account" onPress={onLock} />
        <RowAction label="Unlock" onPress={onUnlock} />
        <RowAction
          label={isArmedResetMfa ? "Click again" : "Reset MFA"}
          tint={isArmedResetMfa ? RED : undefined}
          onPress={onResetMfa}
        />
        <RowAction label="View sessions" onPress={onSessions} />
      </View>
    </View>
  );
}

function RejectedTeamRow({
  u,
  onReinstate,
  isArmed,
}: {
  u: AdminTeamUserRow;
  onReinstate: () => void;
  isArmed: boolean;
}) {
  return (
    <View
      style={{
        padding: 14,
        borderRadius: 6,
        backgroundColor: FOREST_MID,
        borderWidth: 1,
        borderColor: BORDER,
        opacity: 0.85,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
        <Avatar u={u} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: LINEN }} numberOfLines={1}>
            {u.full_name ?? "—"}
          </Text>
          <Text style={{ marginTop: 1, fontFamily: FONTS.body, fontSize: 11.5, color: WS }} numberOfLines={1}>
            {u.email}
          </Text>
          <Text style={{ marginTop: 1, fontFamily: FONTS.body, fontSize: 11, color: WM }} numberOfLines={1}>
            {u.role} · rejected
          </Text>
        </View>
      </View>
      <View style={{ marginTop: 10, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
        <RowAction
          label={isArmed ? "Click again to reinstate" : "Reinstate"}
          tint={isArmed ? SAGE : AMBER}
          onPress={onReinstate}
        />
      </View>
    </View>
  );
}

// Thin wrappers routing visuals through the shared brand components while
// keeping every call site in this large file unchanged.
function RowAction({ label, tint, onPress }: { label: string; tint?: string; onPress: () => void }) {
  const variant = tint === RED ? "destructive" : tint === AMBER || tint === SAGE ? "filled" : "outline";
  return <AdminButton theme={THEME} label={label} variant={variant} size="sm" onPress={onPress} />;
}

function DarkInput({
  value,
  onChange,
  placeholder,
  autoCapitalize,
  keyboardType,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  keyboardType?: "default" | "email-address" | "numeric" | "phone-pad";
}) {
  return (
    <AdminInput
      theme={THEME}
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      autoCapitalize={autoCapitalize}
      keyboardType={keyboardType === "phone-pad" ? "numeric" : keyboardType}
      style={{ marginTop: 6 }}
    />
  );
}
